/**
 * Constellation — the stage map, as a star chart.
 *
 * A VERTICAL zigzag joined by a winding curve, not a row of dots on a rule.
 * The difference matters: a straight line of equal circles reads as a
 * progress bar with extra steps, while a chart that wanders reads as a route
 * with places on it — and the stage you are standing in has somewhere to be.
 *
 * Node states: done (filled), current (ringed and breathing), upcoming (an
 * open outline — a place you have not been yet, NOT a door that is shut).
 * Nothing in the journey is withheld, so nothing in it wears a padlock.
 * The first stage carries extra weight, so the map has a centre of gravity
 * instead of six identical beads.
 *
 * Geometry is in one place below. The whole gutter mirrors under RTL by
 * flipping the x of every node and the curve with it.
 */

const ROW_H = 46;
const GUTTER_W = 46;
/** Node centre x per row — the wander. Longer chains reuse the tail. */
const NODE_XS = [16, 32, 20, 34, 18, 28, 22];
const CURVE_TENSION = ROW_H / 2;

export interface ConstellationNode {
  id: number;
  label: string;
  state: 'done' | 'current' | 'upcoming';
  /** Extra visual weight — the chain's opening chapter. */
  weighted?: boolean;
  /** "2 steps · 1 film" — what this chapter costs, before you commit. */
  meta?: string;
}

/**
 * The node's offset along the gutter, LOGICAL — measured from the reading
 * edge. This is what the dot uses, via `inset-inline-start`, which mirrors
 * itself under RTL.
 */
const nodeX = (i: number) => NODE_XS[Math.min(i, NODE_XS.length - 1)];

/**
 * The same node in the SVG's PHYSICAL coordinates, which always run
 * left-to-right whatever the document direction. Mirroring here is required
 * and must happen in exactly one place: doing it here AND letting the dot's
 * logical property mirror too is a double flip, and the dots drift off the
 * curve they are supposed to sit on by |GUTTER_W - 2x| — up to 22px in
 * Hebrew, measured, while LTR looked perfect.
 */
const nodePhysicalX = (i: number, rtl: boolean) => {
  const x = nodeX(i);
  return rtl ? GUTTER_W - x : x;
};
const nodeY = (i: number) => i * ROW_H + ROW_H / 2;

/** A gentle S between two nodes, bowed with vertical control points. */
function segment(i: number, rtl: boolean): string {
  const x1 = nodePhysicalX(i, rtl);
  const x2 = nodePhysicalX(i + 1, rtl);
  const y1 = nodeY(i);
  const y2 = nodeY(i + 1);
  return `M ${x1} ${y1} C ${x1} ${y1 + CURVE_TENSION}, ${x2} ${y2 - CURVE_TENSION}, ${x2} ${y2}`;
}


const TickGlyph = ({ size }: { size: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
       strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 12.5l4.5 4.5L19 7" />
  </svg>
);

export function Constellation({
  nodes,
  rtl = false,
  celebrate = false,
}: {
  nodes: ConstellationNode[];
  rtl?: boolean;
  /** The finale: light the nodes in sequence rather than all at once. */
  celebrate?: boolean;
}) {
  const height = nodes.length * ROW_H;

  return (
    <div className="journey-const" style={{ height }}>
      <svg
        className="journey-const-curve"
        width={GUTTER_W}
        height={height}
        viewBox={`0 0 ${GUTTER_W} ${height}`}
        aria-hidden
      >
        {nodes.slice(0, -1).map((n, i) => {
          // A segment is "travelled" once the node it leaves is behind you.
          const passed = nodes[i].state === 'done';
          return (
            <path
              key={n.id}
              d={segment(i, rtl)}
              className={`journey-const-seg ${passed ? 'passed' : ''}`}
              style={celebrate ? { animationDelay: `${i * 0.16}s` } : undefined}
            />
          );
        })}
      </svg>

      {nodes.map((n, i) => (
        <div
          key={n.id}
          className={`journey-const-row ${n.state} ${n.weighted ? 'weighted' : ''} ${celebrate ? 'celebrate' : ''}`}
          style={{
            top: nodeY(i) - ROW_H / 2,
            height: ROW_H,
            ['--node-x' as string]: `${nodeX(i)}px`,
            ['--lightup-delay' as string]: `${i * 0.16}s`,
          }}
        >
          <span className="journey-const-dot">
            {n.state === 'done' ? <TickGlyph size={n.weighted ? 13 : 11} /> : null}
          </span>
          <span className="journey-const-text">
            <span className="journey-const-label" dir="auto">{n.label}</span>
            {n.meta && <span className="journey-const-meta">{n.meta}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}
